/**
 * Thin seam over the Firebase Messaging SDK (ONE binding).
 *
 * App init + token mint mechanics live in `@aegix/web-push`;
 * this module keeps ONE's gates and error semantics: initialization is
 * lazy and config-gated — nothing touches Firebase until a caller actually
 * needs a token or a foreground subscription, and a missing env config
 * fails loud in `getOnePushConfig()` and skips the SDK entirely. ONE also
 * drops AIM's Chrome<97 kiosk-WebView guard and the alert-audio plumbing
 * (both AIM-only surfaces).
 */
import { type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  onMessage,
  type MessagePayload,
} from 'firebase/messaging';
import {
  getWebPushFirebaseApp,
  requestFcmRegistrationToken,
} from '@aegix/web-push';
import { getOnePushConfig } from './config';

let app: FirebaseApp | null = null;

function isPushCapableBrowser(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
}

function getAppInstance(): FirebaseApp | null {
  if (app) return app;
  if (!isPushCapableBrowser()) return null;
  const config = getOnePushConfig();
  if (!config) return null; // already failed loud
  try {
    app = getWebPushFirebaseApp(config.firebase);
  } catch (err) {
    console.error('[aegix-one push] Firebase initialization failed:', err);
    return null;
  }
  return app;
}

/**
 * Mint (or fetch the cached) FCM registration token for this browser.
 * Surfaces the browser's notification-permission prompt on first call after
 * login when permission is still `default`. Returns `null` when push is
 * unavailable, config is missing (failed loud upstream), or the user has
 * denied notifications (their choice — quiet skip).
 */
export async function requestFcmToken(): Promise<string | null> {
  const instance = getAppInstance();
  if (!instance) return null;
  if (Notification.permission === 'denied') return null;
  const config = getOnePushConfig();
  if (!config) return null;
  try {
    return await requestFcmRegistrationToken({
      app: instance,
      vapidKey: config.vapidKey,
    });
  } catch (err) {
    const code = (err as { code?: string })?.code;
    if (code === 'messaging/permission-blocked') {
      // User declined the prompt — expected, not an error.
      return null;
    }
    console.warn(
      '[aegix-one push] Firebase messaging token error:',
      code || (err as Error)?.message,
    );
    return null;
  }
}

/**
 * Subscribe to foreground FCM messages (e.g. silent `token_refresh` pushes
 * delivered while a ONE tab is focused). Returns an unsubscribe function;
 * a no-op unsubscribe when push is unavailable.
 */
export function subscribeForegroundMessages(
  handler: (payload: MessagePayload) => void,
): () => void {
  const instance = getAppInstance();
  if (!instance) return () => undefined;
  try {
    return onMessage(getMessaging(instance), handler);
  } catch (err) {
    console.error('[aegix-one push] Firebase initialization failed:', err);
    return () => undefined;
  }
}
